import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApproveTask, useRejectTask } from "@/hooks/use-my-tasks";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { SrIcon } from "@/components/ui/SrIcon";
import { ProjectChip } from "./ProjectChip";
import { HastBadge } from "./HastBadge";
import { InboxAvatar } from "./InboxAvatar";
import type { InboxApproval } from "@/hooks/use-pm-inbox";

interface Props {
  task: InboxApproval;
}

export function RowGodkjenning({ task: t }: Props) {
  const { activeUser } = useImpersonation();
  const approveMut = useApproveTask();
  const rejectMut = useRejectTask();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const handleApprove = () => {
    if (!activeUser?.id) return;
    approveMut.mutate({ assignmentId: t.id, approvedBy: activeUser.id });
  };

  const handleReject = () => {
    if (!reason.trim()) return;
    rejectMut.mutate(
      { assignmentId: t.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setRejectOpen(false);
          setReason("");
        },
      },
    );
  };

  return (
    <>
      {/* VIB-959 (fase 2): desktop-grid kun fra xl:, mobilkort under. Ansatt-
          flyt A-funn — PM-innboksen hadde ingen mobiltilpasning. */}
      <div className="hidden xl:grid grid-cols-[24px_1fr_auto_auto_auto] items-center gap-3.5 px-5 py-3.5 border-t border-border/40">
        <InboxAvatar fullName={t.fromUserName} avatarUrl={t.fromUserAvatar} size={24} />
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1">
            <ProjectChip
              projectId={t.projectId}
              projectCode={t.projectCode}
              projectName={t.projectName}
              customerName={t.customerName}
            />
            <HastBadge ageDays={t.ageDays} />
          </div>
          <div className="text-sm font-medium truncate">{t.taskDescription}</div>
          <div className="text-xs text-muted-foreground">
            {t.fromUserName}
            {t.phase ? ` · ${t.phase}` : ""}
          </div>
        </div>
        <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{t.hours}t</span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => setRejectOpen(true)}
        >
          Avvis
        </Button>
        <Button
          size="sm"
          className="h-8 text-xs"
          onClick={handleApprove}
          disabled={approveMut.isPending}
        >
          <SrIcon name="check" className="w-3 h-3 mr-1" />
          Godkjenn
        </Button>
      </div>

      <div className="xl:hidden px-4 py-3 border-t border-border/40 space-y-2.5">
        <div className="flex items-start gap-2.5">
          <InboxAvatar fullName={t.fromUserName} avatarUrl={t.fromUserAvatar} size={28} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <ProjectChip
                projectId={t.projectId}
                projectCode={t.projectCode}
                projectName={t.projectName}
                customerName={t.customerName}
              />
              <HastBadge ageDays={t.ageDays} />
            </div>
            <div className="text-sm font-medium">{t.taskDescription}</div>
            <div className="text-xs text-muted-foreground">
              {t.fromUserName}
              {t.phase ? ` · ${t.phase}` : ""}
            </div>
          </div>
          <span className="text-sm font-semibold tabular-nums whitespace-nowrap shrink-0">{t.hours}t</span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-10 text-xs"
            onClick={() => setRejectOpen(true)}
          >
            Avvis
          </Button>
          <Button
            size="sm"
            className="flex-1 h-10 text-xs"
            onClick={handleApprove}
            disabled={approveMut.isPending}
          >
            <SrIcon name="check" className="w-3.5 h-3.5 mr-1" />
            Godkjenn
          </Button>
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Avvis oppgave</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Skriv en tilbakemelding til {t.fromUserName} om hva som må gjøres.
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Tilbakemelding..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRejectOpen(false)}>
              Avbryt
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleReject}
              disabled={!reason.trim() || rejectMut.isPending}
            >
              Avvis oppgave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
